export interface PricingTier {
  name: string;
  price: string;
  description: string;
  features: string[];
  addons?: string[];
  highlighted?: boolean;
}

export interface ServiceFAQ {
  q: string;
  a: string;
}

export interface Service {
  id: string;
  title: string;
  subtitle: string;
  price: string;
  color: string;
  emoji: string;
  tags: string[];
  shortDesc: string;
  longDesc: string[];
  process: { step: string; title: string; desc: string }[];
  pricingTiers: PricingTier[];
  faqs: ServiceFAQ[];
  vgenUrl?: string;
  shorts?: string[];
  gallery?: { src: string; caption?: string }[];
}

const services: Service[] = [
  {
    id: '3d-modelling',
    title: '3D Modelling',
    subtitle: 'VRM / Warudo / VRChat / Props',
    price: 'From $200',
    vgenUrl: 'https://vgen.co/panekopanko',
    shorts: ['vmdaRq7n9BQ', 'cP-MsXcKots'],
    gallery: [
      // { src: '/images/gallery/lowpoly-1.webp', caption: 'Front view' },
      // { src: '/images/gallery/lowpoly-2.webp', caption: 'Expression sheet' },
      // { src: '/images/gallery/lowpoly-3.webp', caption: 'Physics demo' },
    ],
    color: '#ff6eb4',
    emoji: '🎨',
    tags: ['Blender', 'Warudo', 'VRM', 'Unity', 'VRChat'],
    shortDesc:
      'Custom VTuber avatars, props, and environment assets. Fully rigged and expression-ready models delivered in VRM, FBX, or blend format.',
    longDesc: [
      "I've been crafting 3D models for VTubers and the VRChat community since 2025. Every model starts with a concept call where we nail down your style, lore, and technical requirements before a single polygon is placed.",
      "My workflow covers the full pipeline: base mesh in Blender, high-detail sculpting in ZBrush, retopology, UV unwrapping, PBR texturing, rigging, blend shape / facial expression setup, and final Unity/VRM export. I specialise in the anime-adjacent aesthetic popular in the VTubing scene but can work in any style.",
      "Deliverables always include source files so you're never locked in — your model is yours completely.",
    ],
    process: [
      { step: '01', title: 'Discovery Call', desc: 'We discuss your character concept, reference images, lore, and technical requirements (platform, poly budget, expression needs).' },
      { step: '02', title: 'Concept & Blockout', desc: 'I produce a rough blockout / concept sheet for approval before investing in detail work. Up to 2 revision rounds at this stage.' },
      { step: '03', title: 'Modelling & Sculpting', desc: 'Full-detail modelling in Blender + ZBrush. I share WIP renders at key milestones so you can course-correct early.' },
      { step: '04', title: 'Rigging & Expressions', desc: 'Full-body armature, IK/FK, cloth & hair physics, and facial blend shapes / expression presets for your chosen platform.' },
      { step: '05', title: 'Texturing & Polish', desc: 'PBR textures, custom toon shaders, and final lighting / render checks. VRM or platform-specific export and optimisation.' },
      { step: '06', title: 'Delivery & Aftercare', desc: 'All source files, exports, and a handover doc. 14 days of minor bug-fix support included.' },
    ],
    pricingTiers: [
      {
        name: 'Custom',
        price: 'On request',
        description: 'Have something specific in mind? Let\'s talk.',
        features: [
          'Scope and deliverables agreed per project',
          'Chibi models, props, accessories, scene assets, and more',
          'Any style — furry, mecha, magical girl, non-standard characters',
          'Platform of your choice (VRM, VRChat, Warudo, game-ready, etc.)',
          'Warudo setup included where applicable',
          'Onboarding call — I walk you through the full configuration',
        ],
      },
      {
        name: 'Low Poly',
        price: 'From $200',
        description: 'Stylised low-poly VTuber avatar optimised for performance',
        highlighted: true,
        features: [
          'Optimised low-poly mesh',
          'Fully rigged & textured',
          'VRM + VRChat avatar 3.0 UnityPackage',
          'Warudo compatible',
          'Physics setup (VRM Springbone / VRC Physicsbone)',
          '6 custom facial expressions',
          'VRC Visemes',
          'ARKit PerfectSync blendshapes — evaluated per model type',
          'PNG texture files',
          'Blender source file',
          'Warudo setup included',
          'Onboarding call — I walk you through the full configuration',
        ],
      },
      {
        name: 'High Poly',
        price: '$4000+',
        description: 'Studio-quality VTuber avatar — commissions currently closed',
        features: [
          'Fully rigged & textured high-poly model',
          '.Warudo, .VRM, .FBX, .blend + PNG textures, VRChat 3.0 UnityPackage',
          'VRC & Warudo compatible',
          'Physics: VRM Springbone + VRC Physicsbone',
          '6 custom facial expressions',
          'ARKit facial tracking blendshapes',
          'Live2D eye jiggle',
          'Magica clothes setup + eye jiggle (Warudo)',
          'VRC Visemes',
          'ARKit PerfectSync blendshapes — evaluated per model type',
          'Warudo setup included',
          'Onboarding call — I walk you through the full configuration',
          'General range $5,000–$8,000+ depending on complexity',
        ],
        addons: [
          'Basebody +$200',
          'Props +$100 each',
          'NiloToonURP Shader Setup +$200',
          'Additional outfits or hairstyles — from half the base price',
        ],
      },
    ],
    faqs: [
      { q: 'Are commissions open?', a: 'High poly commissions are currently closed. Please read the TOS before applying — links are in the commission form.' },
      { q: 'What files will I receive?', a: 'High poly commissions include a .Warudo file, .VRM, .FBX, Blender source file with PNG textures, and a VRChat avatar 3.0 UnityPackage.' },
      { q: 'What does the price range depend on?', a: 'High poly avatars generally run $5,000–$8,000+ depending on design complexity, number of outfits, and technical requirements.' },
      { q: 'Do you do corporate commissions?', a: 'Yes, but at a different rate. Please contact me directly to discuss.' },
      { q: 'Can I get a referral for high-level technical support?', a: 'Yes — for advanced technical needs I can refer you to a trusted partner at Mofu Workshop.' },
      { q: 'Can I commission a model based on existing art?', a: 'Yes! Clear reference sheets speed up the process significantly.' },
    ],
  },
  {
    id: 'vtubing-setup',
    title: 'Live2D',
    subtitle: 'Art, rigging, PerfectSync & more',
    price: 'On request',
    color: '#c97cff',
    emoji: '🎙️',
    tags: ['Live2D', 'VTube Studio', 'VBridger', 'PerfectSync', 'Cubism'],
    shortDesc:
      'Full Live2D services — illustration, rigging, PerfectSync VBridger setup, model evaluation, and layer cutting. Commission art and rig together for the best result.',
    longDesc: [
      "I offer the full Live2D pipeline, from character illustration through to a finished, streaming-ready rig. Commissioning art and rigging together means the illustration is drawn with rigging in mind from the start — the right layer separation, deformation-friendly shapes, and proportions that move naturally. This produces significantly better results than rigging artwork that wasn't designed for it.",
      "If you already have finished art, I can rig it, evaluate your existing model, cut your PSD layers for rigging, or add VBridger PerfectSync support for iPhone face tracking with 52 ARKit blendshapes.",
      "Every rig is built in Live2D Cubism and delivered ready for VTube Studio, with physics tuned and expressions configured.",
    ],
    process: [
      { step: '01', title: 'Scope & Brief', desc: 'We define what you need — art only, rig only, or the full package. If art is included, we nail down character design, expressions, and outfit details.' },
      { step: '02', title: 'Illustration (if included)', desc: 'Character art drawn with Live2D in mind — correct layer structure, deformation-friendly shapes, and clean PSD ready for cutting.' },
      { step: '03', title: 'Layer Cutting', desc: 'PSD layers are separated, named, and organised for the rig. This stage can be commissioned standalone if you have existing art.' },
      { step: '04', title: 'Rigging', desc: 'Full rig in Live2D Cubism — head & body movement, physics on hair, clothes, and accessories, and all agreed expressions.' },
      { step: '05', title: 'VBridger / PerfectSync (if included)', desc: 'ARKit blendshapes added and mapped for VBridger, enabling 52-point iPhone face tracking via VTube Studio.' },
      { step: '06', title: 'Delivery & Setup', desc: 'Delivered ready for VTube Studio with physics tuned, expressions configured, and a handover call to get you live.' },
    ],
    pricingTiers: [
      {
        name: 'Rig Only',
        price: 'On request',
        description: 'You provide the layered PSD, I rig it',
        features: [
          'Full Live2D rig in Cubism',
          'Head & body movement',
          'Hair, cloth & accessory physics',
          'Expressions (number agreed at brief)',
          'VTube Studio ready delivery',
          'Physics tuning & expression config',
        ],
      },
      {
        name: 'Add-On Services',
        price: 'On request',
        description: 'Standalone services for existing models or artists',
        features: [
          'Layer cutting — PSD separation & prep for rigging',
          'Model evaluation — review of an existing rig with improvement notes',
          'VBridger PerfectSync — 52 ARKit blendshape setup for iPhone face tracking',
          'Re-rigging — replacing or improving an existing rig',
        ],
      },
      {
        name: 'Art & Rig',
        price: 'On request',
        description: 'Full pipeline — illustration drawn for Live2D, then rigged',
        features: [
          'Character illustration designed for Live2D from the ground up',
          'Correct layer structure and deformation-friendly shapes baked in',
          'Layer cutting included',
          'Full Live2D rig with physics and expressions',
          'Optional VBridger PerfectSync add-on',
          'VTube Studio ready delivery with handover call',
          'Best results — art and rig designed together',
        ],
        highlighted: true,
      },
    ],
    faqs: [
      { q: 'Why commission art and rig together?', a: "Artwork that wasn't drawn with Live2D in mind is harder to rig and produces worse results — parts that should move independently are merged, shapes are too complex to deform cleanly, and proportions don't translate well to motion. When I draw the illustration, every layer and shape is planned around how it will be rigged." },
      { q: 'What is layer cutting?', a: "Live2D rigging requires the artwork to be split into many individual layers — each moving part (eyelid, strand of hair, collar, sleeve) needs its own layer. Cutting is the process of separating, cleaning, and naming those layers correctly. It can be commissioned as a standalone service if you have artwork you want rigged elsewhere, or it's included in the Art & Rig package." },
      { q: 'What is VBridger PerfectSync?', a: "VBridger is a plugin for VTube Studio that uses your iPhone's Face ID camera to drive 52 ARKit blendshapes, capturing fine movements like eyebrow raises, cheek puffs, jaw direction, and tongue. This is a significant step up from standard webcam tracking. It requires an iPhone with Face ID and can be added to any rig as a standalone service." },
      { q: 'What is a model evaluation?', a: "An evaluation is a detailed review of your existing Live2D model — I go through the rig, physics, and expressions and produce a written report covering what's working well, what could be improved, and how. Useful if you want to understand your model before commissioning changes, or if you're considering a re-rig." },
      { q: 'Do I need to provide art for a rig-only commission?', a: 'Yes — rig-only commissions require a layered PSD with parts already separated, or you can add layer cutting as an extra step.' },
      { q: 'Can you re-rig a model I already have?', a: 'Yes. The cost depends on the complexity of the model and the state of the existing rig and source files.' },
    ],
  },
  {
    id: 'pcb-design',
    title: 'Hardware & Badges',
    subtitle: 'Art badges, PCB design & conference hardware',
    price: 'From €160',
    color: '#ff9fd0',
    emoji: '📛',
    tags: ['KiCad', 'JLCPCB', 'RP2040', 'Conference Badge', 'SAO', 'Firmware'],
    shortDesc:
      'Custom art badges for conferences and events — PCBs with hand-drawn artwork, interactive electronics, SAO headers, and open-source firmware. Also general PCB design and hardware consulting.',
    longDesc: [
      "Conference badges are my favourite thing to make. A great badge is part art print, part functional hardware — it tells a story, survives a lanyard, and ideally does something interesting. I design the PCB, draw the artwork directly into the silkscreen and copper layers, wire up LEDs or a small MCU, and ship Gerbers that look as good in your hand as they do in KiCad.",
      "My badges have shipped to DEF CON, hackathons, and small community events. I design for JLCPCB or PCBWay production, include SAO (Shitty Add-On / Simple Add-On) headers by default, and can add interactivity ranging from blinky LEDs to RP2040-powered mini-games or NFC payloads.",
      "Beyond badges I do general PCB design — custom keyboards, IoT devices, wearables, and RF hardware — with a bias toward RP2040, STM32, and ESP32 platforms. Open-source by default under CERN-OHL unless you need IP protection.",
    ],
    process: [
      { step: '01', title: 'Brief & Art Direction', desc: 'We talk about the event, the vibe, your character or brand, interactivity requirements, and quantity. Badge design lives or dies on the brief.' },
      { step: '02', title: 'Artwork & Concept', desc: 'I sketch the badge artwork and layout — silkscreen illustration, copper fill shapes, soldermask colour choices, and component placement all designed together as one piece.' },
      { step: '03', title: 'Schematic & BOM', desc: 'Full schematic in KiCad with component selection from LCSC/Mouser. SAO header included. MCU and interactive elements scoped here.' },
      { step: '04', title: 'PCB Layout & Art', desc: 'Artwork is translated into KiCad layers — silk, copper, soldermask cutouts. Gerber preview shared for approval before fab order.' },
      { step: '05', title: 'DFM & Fab', desc: 'DRC/ERC clean pass and DFM review against JLCPCB/PCBWay. I can place the order on your behalf or hand off production-ready files.' },
      { step: '06', title: 'Firmware (optional)', desc: 'RP2040 / ATtiny / ESP32 firmware for interactive badges — LED animations, button interactions, NFC payloads, or SAO communication.' },
    ],
    pricingTiers: [
      {
        name: 'Art Badge',
        price: '€160',
        description: 'Illustrated PCB badge with LEDs — no MCU',
        features: [
          'Custom silkscreen & copper artwork',
          'Soldermask colour selection',
          'SAO v1.69bis header included',
          'LED circuit (up to 8 LEDs)',
          'JLCPCB-ready Gerbers + BOM',
          'DRC verified',
          'Up to 2 revision rounds on artwork',
        ],
      },
      {
        name: 'Interactive Badge',
        price: '€360',
        description: 'MCU-powered badge with firmware',
        highlighted: true,
        features: [
          'Everything in Art Badge',
          'RP2040 or ATtiny MCU',
          'Button interactions or sensors',
          'LED animations / WS2812B support',
          'NFC payload (optional)',
          'Open-source firmware (CircuitPython or C)',
          'Assembly files (CPL + BOM) for JLCPCB SMT',
          'DFM review',
        ],
      },
      {
        name: 'Event Run',
        price: 'On request',
        description: 'Design + production coordination for a full badge run',
        features: [
          'Full interactive badge design',
          'Production liaison with JLCPCB / PCBWay',
          'Volume BOM optimisation',
          'Lanyard hole, breakaway tabs, or custom shape',
          'Packaged firmware + flashing instructions',
          'Optional: NFC, SAO ecosystem integration',
          'NDA available',
        ],
      },
    ],
    faqs: [
      { q: 'What is a SAO header?', a: "SAO (Simple Add-On, originally 'Shitty Add-On') is a small standard connector used on hacker conference badges to attach accessory boards. I include the SAO v1.69bis header on all badges by default so they play well in the broader badge ecosystem." },
      { q: 'What\'s the minimum order quantity?', a: 'JLCPCB minimum is 5 PCBs, and the price per unit drops fast at 10, 30, and 100+. I design with MOQ in mind and can advise on whether a run makes sense for your event size.' },
      { q: 'Can you make a badge shaped like my character?', a: "Yes — custom PCB outlines are fully supported. I'll design the board edge as part of the artwork. Unusual shapes may affect per-unit cost at the fab." },
      { q: 'Do you add NFC to badges?', a: 'Yes. I can wire in an NXP NFC tag IC and program a URL, vCard, or custom NDEF payload — great for business-card badges or puzzle challenges.' },
      { q: 'Can I use this for a DEF CON badge challenge?', a: 'Absolutely — that\'s the natural habitat. I can design challenge mechanics into the hardware and firmware if you want something to solve.' },
      { q: 'Do you do general PCB work outside of badges?', a: 'Yes — keyboards, IoT, wearables, RF hardware. Reach out and I\'ll quote it. Badge design is just my favourite part.' },
    ],
  },
  {
    id: 'security-research',
    title: 'Security',
    subtitle: 'Reach out to discuss',
    price: 'By arrangement',
    color: '#ff6eb4',
    emoji: '🔐',
    tags: ['CTF', 'Research', 'Hardware', 'Consulting'],
    shortDesc:
      'Security work of various kinds. Reach out directly to discuss scope and rates.',
    longDesc: [
      "I do security work. The specifics depend on what you need and whether it's something I can help with — reach out and we'll figure it out from there.",
      "CTF stuff, hardware things, research, consulting. Some of it I can talk about publicly, some of it I can't. NDA available.",
    ],
    process: [
      { step: '01', title: 'Reach Out', desc: 'Send a message describing what you need. No forms, no intake questionnaires — just a message.' },
      { step: '02', title: 'Discussion', desc: 'We talk through scope, constraints, and whether I\'m the right fit. If not, I\'ll point you somewhere better.' },
      { step: '03', title: 'Arrangement', desc: 'If it\'s a go, we agree terms and get started. NDA available if needed.' },
    ],
    pricingTiers: [
      {
        name: 'Consulting',
        price: 'On request',
        description: 'Advice, review, or a conversation',
        features: [
          'Flexible scope',
          'NDA available',
          'Contact to discuss',
        ],
      },
      {
        name: 'Engagement',
        price: 'On request',
        description: 'Longer-form work',
        highlighted: true,
        features: [
          'Scope agreed upfront',
          'NDA available',
          'Contact to discuss',
        ],
      },
      {
        name: 'Training',
        price: 'On request',
        description: 'Individual or team',
        features: [
          'CTF coaching',
          'Workshops',
          'Contact to discuss',
        ],
      },
    ],
    faqs: [
      { q: 'What kind of security work do you do?', a: 'Various things. Reach out and describe what you need — I\'ll let you know if it\'s something I can help with.' },
      { q: 'Is NDA available?', a: 'Yes.' },
      { q: 'How do I get in touch?', a: 'Discord or the contact form. A brief description of what you need is enough to start.' },
    ],
  },
  {
    id: 'vtuber-support',
    title: 'VTuber Support',
    subtitle: 'Setup, branding & web',
    price: 'On request',
    color: '#ff9fd0',
    emoji: '🛠️',
    tags: ['OBS', 'Web', 'Branding', 'Chatbox', 'VTube Studio'],
    shortDesc:
      'Practical help for VTubers — OBS configuration, custom chatboxes, web design, business cards, stream branding, and general setup support.',
    longDesc: [
      "Not everything about being a VTuber is art and rigging. There's a lot of technical and creative groundwork that needs to happen before you go live — and it can be overwhelming to handle alone.",
      "I offer a range of support services covering the practical side: getting OBS configured correctly, building a custom chatbox with CSS/JS, designing a personal website or landing page, creating business cards and press kit assets, and general troubleshooting or consulting for your streaming setup.",
      "These are flexible, scoped-per-project services. Reach out with what you need and I'll put together a quote.",
    ],
    process: [
      { step: '01', title: 'Brief', desc: 'Tell me what you need — a quick fix, a full setup, or something in between. I\'ll ask a few questions to scope it correctly.' },
      { step: '02', title: 'Quote', desc: 'I send a clear quote with scope, deliverables, and timeline. No surprises.' },
      { step: '03', title: 'Delivery', desc: 'Work is delivered with documentation or a walkthrough call so you understand what was done and how to maintain it.' },
    ],
    pricingTiers: [
      {
        name: 'OBS & Streaming',
        price: 'On request',
        description: 'Get your stream configured and running properly',
        features: [
          'OBS scene setup and configuration',
          'VTube Studio / Warudo integration',
          'Alert and overlay wiring (StreamElements, Streamlabs, etc.)',
          'Audio routing and monitoring setup',
          'Stream troubleshooting and consultation',
        ],
      },
      {
        name: 'Web & Branding',
        price: 'On request',
        description: 'Digital presence and print-ready assets',
        features: [
          'Personal website or landing page',
          'Custom chatbox (CSS + JS)',
          'Business cards (print-ready)',
          'Press kit / media kit layout',
          'Social media banners and profile assets',
        ],
        highlighted: true,
      },
      {
        name: 'Consulting',
        price: 'On request',
        description: 'Advice, planning, and general VTuber support',
        features: [
          'VTuber debut planning and checklist',
          'Software stack recommendations',
          'General setup help and Q&A',
          'Platform strategy (Twitch vs YouTube vs TikTok)',
          'Referrals to trusted partners for work outside my scope',
        ],
      },
    ],
    faqs: [
      { q: 'What is a custom chatbox?', a: 'A chatbox is the on-screen widget that shows your live chat during a stream. A custom one is built with HTML, CSS, and JavaScript and styled to match your branding — instead of using a default widget that looks the same as everyone else\'s.' },
      { q: 'Do you build full websites?', a: 'Yes — from simple one-page landing sites to multi-section portfolio and commission pages. I can work with your existing domain and hosting, or recommend setup if you\'re starting from scratch.' },
      { q: 'Can you help me set up OBS for the first time?', a: 'Yes. I can configure scenes, sources, audio routing, alerts, and VTuber software integration from scratch, and walk you through it on a call so you know how to manage it yourself going forward.' },
      { q: 'Do you do business cards for VTubers?', a: 'Yes — print-ready business card designs in standard sizes, tailored to your character and brand. Great for conventions and networking.' },
      { q: 'What if I need something not listed here?', a: "Reach out and describe what you need. If it's within scope I'll quote it; if not I'll point you to someone who can help." },
    ],
  },
  {
    id: 'merch-design',
    title: 'Merch Design',
    subtitle: 'Print-ready artwork for your brand',
    price: 'On request',
    color: '#c97cff',
    emoji: '🎁',
    tags: ['Illustration', 'Print', 'Apparel', 'Stickers', 'Acrylic'],
    shortDesc:
      'Original merch artwork designed for print — apparel, stickers, acrylic stands, keychains, and more. Designed to sell.',
    longDesc: [
      "Good merch starts with good art. I design original illustrations and graphic assets built specifically for merchandise — with production constraints, print specs, and your audience in mind.",
      "Whether you need a full merch drop with multiple items or a single design for stickers, I handle the artwork from concept through to print-ready files. I can work from your existing character design or develop something new.",
      "Files are delivered in the formats your manufacturer requires, with colour profiles, bleed, and resolution set up correctly so nothing looks wrong when it arrives.",
    ],
    process: [
      { step: '01', title: 'Brief', desc: 'We discuss what you want to make, your character or brand, and the manufacturer or platform you\'re using (e.g. Printful, Kotobukiya, local printer).' },
      { step: '02', title: 'Concept', desc: 'Rough sketches or composition options for approval before committing to detailed artwork.' },
      { step: '03', title: 'Illustration', desc: 'Final artwork produced to spec — correct resolution, colour mode, and format for the intended product.' },
      { step: '04', title: 'Delivery', desc: 'Print-ready files delivered in agreed formats (PNG, PDF, AI, etc.) with any required bleed and safe zones set up.' },
    ],
    pricingTiers: [
      {
        name: 'Stickers & Prints',
        price: 'On request',
        description: 'Single designs for flat print products',
        features: [
          'Sticker sheets, individual stickers',
          'Art prints and posters',
          'Postcards and bookmarks',
          'Print-ready PNG / PDF delivery',
          'Colour-profile correct for print',
        ],
      },
      {
        name: 'Apparel & Goods',
        price: 'On request',
        description: 'Wearables and physical merchandise',
        features: [
          'T-shirts, hoodies, tote bags',
          'Acrylic stands and keychains',
          'Enamel pin design (ready for manufacturer)',
          'Files set up for your chosen manufacturer',
          'Front + back / multi-placement designs',
        ],
        highlighted: true,
      },
      {
        name: 'Full Merch Drop',
        price: 'On request',
        description: 'Cohesive artwork across a complete product line',
        features: [
          'Multiple products with consistent design language',
          'Concept development from scratch',
          'Character or mascot illustration if needed',
          'All print-ready files included',
          'Mockup assets for promotion',
        ],
      },
    ],
    faqs: [
      { q: 'Do you design for specific manufacturers?', a: "Yes — if you tell me where you're printing (Printful, Gelato, a local print shop, a manufacturer for acrylics etc.) I\'ll set files up to their exact specs." },
      { q: 'Can you design merch based on my existing VTuber model?', a: 'Yes — I can work from your 3D model, Live2D model, or reference art to create merch-specific illustrations.' },
      { q: 'Do you produce or fulfil the merch?', a: 'It depends! — I can deliver artwork files and also handle production and fulfilment, depends how you like to fix with it! I can liase with the chosen manufacturer or merch platform.' },
      { q: 'Can you create a new character or mascot for merch?', a: 'Yes, original character design for merchandise is available as part of a Full Merch Drop commission.' },
      { q: 'What file formats do you deliver?', a: 'Typically high-res PNG and PDF, with layered source files if needed. I\'ll match whatever your manufacturer requires.' },
    ],
  },
];

export default services;
